import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { clsx } from "clsx";
import { EllipsisVerticalIcon } from "@heroicons/react/24/solid";
import { AlertCircle, CheckCircle2, Clock } from "lucide-react";
import useUserAccount from "./userAccountHook";
import useRoute from "./routesHook";
import Loan from "../models/Loan";
import { formatCurrencyAmount, getFormattedDate, getFormattedPaymentChannel, showToast } from "../utils/miscFunctions";
import PopOnMe from "../components/elements/poponme/PopOnMe";
import { ITableAction } from "../components/elements/GTable/GTable";
import { addLoansToDisbursement, removeLoanFromDisbursement } from "../services/disbursements";
import { toast } from "react-toastify";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { approveSingleLoan, generateLoanAgreementDocuments, getLoanPaymentHistoryByReference, regeneratePaymentHistory, rejectSingleLoan } from "../api/requests";
import useModal from "./modalHook";
import moment from "moment";
import useDisbursements from "../hooks/disbursementHook";
import PaymentStatus from "../components/PaymentStatus";
import ActionConfirmation from "../components/elements/ActionConfirmation";
import RejectLoanModal from "../views/partners/financier/loans/components/RejectLoanModal";
import { deleteLoanById, getLoanById, markLoanAsPaid } from "../services/financing";
import Disbursement from "../models/Disbursement";
import { verifyLoanAndReconcileLoanStatus } from "../services/loans";


interface IUseLoans {
    tableRowActions?: (loan: Loan) => ITableAction[];
    showColumCheckbox?: boolean;
}

const useLoans = ({ tableRowActions, showColumCheckbox }: IUseLoans) => {
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const LoanModalComponent = ModalComponent;
    const [disbursementDetails, setDisbursementDetails] = useState<Disbursement>();
    const { fetchDisbursementById } = useDisbursements({});
    const { user } = useUserAccount();
    const isInternalAdmin = user?.isInternalAdmin?.();
    const queryClient = useQueryClient();
    const { getDashboardBase } = useRoute();
    const navigate = useNavigate();
    const [loanDetails, setLoanDetails] = useState<Loan>();
    const { loanId } = useParams();
    const loanIdNumber = loanDetails?.id || loanId;
    const loanReference = loanDetails?.reference;
    const loanApplicationNumber = loanDetails?.applicationNumber;
    const invalidateQueries = () => { queryClient.invalidateQueries({ queryKey: ['getSingleLoan'] }) }

    const { data: paymentHistory, isLoading: paymentHistoryLoading } = useQuery({
        queryKey: ['paymentHistory', loanReference],
        queryFn: () => {
            if (!loanReference) return Promise.resolve([]);
            return loanApplicationNumber && fetchLoanPaymentHistoryByReference(loanApplicationNumber);
        },
        enabled: !!loanReference
    });

    const { isLoading: loanDetailsLoading } = useQuery({ queryKey: ['getSingleLoan'], queryFn: () => getSingleLoan() })

    const allInitialAmounts = paymentHistory && paymentHistory?.filter((p: any) => p.planType === 'INITIAL_AMOUNT');
    const initialAmount = allInitialAmounts?.[allInitialAmounts.length - 1];

    const getRouteFromProductType = (loan: Loan) => {
        const productType = loan?.productType?.displayName;
        if (!loan?.reference) return `${getDashboardBase()}/loans`;
        switch (productType) {
            case 'Motor Insurance':
                return `${getDashboardBase()}/policies/${loan?.reference}`;
            case 'Premium Financing':
                return `${getDashboardBase()}/premium-financing/${loan?.reference}`;
            default:
                return `${getDashboardBase()}/loans/${loan.id}`;
        }
    }

    const ApprovalStatusBadge = ({ status }: { status: boolean | null }) => {
        const getStatusInfo = () => {
            if (status === null) {
                return {
                    color: 'border border-yellow-300 text-yellow-800', icon: Clock, text: 'In Review'
                };
            } else if (status === true) {
                return {
                    color: 'border border-green-300 text-green-800', icon: CheckCircle2, text: 'Approved'
                };
            } else {
                return {
                    color: 'border border-red-300 text-red-800', icon: AlertCircle, text: 'Rejected'
                };
            }
        };

        const { color, icon: Icon, text } = getStatusInfo();

        return (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full dar ${color}`}>
                <Icon className="w-3 h-3 mr-1" />
                <span className="capitalize">{text}</span>
            </span>
        );
    };

    const columns = useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    ...(showColumCheckbox ? [{
                        id: 'selection',
                        Header: ({ getToggleAllRowsSelectedProps }: any) => (
                            <div className="">
                                <input type="checkbox" {...getToggleAllRowsSelectedProps()} />
                            </div>
                        ),
                        Cell: ({ row }: any) => (
                            <div className="">
                                <input type="checkbox" {...row.getToggleRowSelectedProps()} />
                            </div>
                        )
                    }] : []),
                    {
                        Header: 'Application No.',
                        accessor: 'applicationNumber',
                        Cell: (row: any) => {
                            return (
                                <Link to={`${getDashboardBase()}/loans/${row.row.original.id}`}>
                                    <span className={` ${row.row.original?.reference ? 'text-link-main hover:underline dark:text-darkBg-textLink dark:hover:text-darkBg-textLink' : ''}`}>{row.cell.value ?? '-'}</span>
                                </Link>
                            );
                        }
                    },
                    {
                        Header: 'Applicant',
                        accessor: 'customer.fullName',
                        Cell: (row: any) => {
                            const customerId = row.row.original?.customer?.id;

                            return <Link to={`${getDashboardBase()}/customers/${customerId}`}>
                                <span className={` ${row.row.original?.reference ? 'text-link-main hover:underline dark:text-darkBg-textLink dark:hover:text-darkBg-textLink' : ''}`}>{row.cell.value ? row.cell.value : '-'}</span>
                            </Link>
                        }
                    },
                    {
                        Header: 'Principal',
                        accessor: 'principal',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? formatCurrencyAmount?.(row.cell.value) : '-'}</span>
                        }
                    },
                    {
                        Header: 'Interest',
                        accessor: 'interest',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? formatCurrencyAmount?.(row.cell.value) : '-'}</span>
                        }
                    },
                    {
                        Header: 'Repayment amount',
                        accessor: 'repaymentAmount',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? formatCurrencyAmount?.(row.cell.value) : '-'}</span>
                        }
                    },
                    {
                        Header: 'Requester',
                        accessor: 'requester',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value?.name : '-'}</span>
                        }
                    },
                    ...(isInternalAdmin ? [{
                        Header: 'Financer',
                        accessor: 'financer',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value?.name : '-'}</span>
                        }
                    }] : []),
                    {
                        Header: 'Term',
                        accessor: 'period',
                        Cell: (row: any) => {
                            return <span className="capitalize">{row.cell.value ? row.cell.value + ' Month' + `${Number(row.cell.value) > 1 ? 's' : ''}` : '-'}</span>
                        }
                    },
                    {
                        Header: 'Applied On',
                        accessor: 'createdOn',
                        Cell: (row: any) => {
                            return <span>{getFormattedDate({ date: row.cell.value })}</span>;
                        }
                    },
                    {
                        Header: 'Is Disbursed',
                        accessor: 'disbursed',
                        Cell: (row: any) => {
                            return (
                                <div className="w-full flex text-white">
                                    <span
                                        className={clsx(
                                            'w-[30px] h-[30px] rounded-full flex items-center justify-center text-[0px] ',
                                            { ['bg-status-active']: row.row.original?.disbursed === true },
                                            { ['bg-status-ended']: row.row.original?.disbursed === false }
                                        )}>
                                        {row.row.original?.disbursed}
                                    </span>
                                </div>
                            );
                        }
                    },
                    {
                        Header: 'Disbursed On',
                        accessor: 'disbursedOn',
                        Cell: (row: any) => {
                            return <span>{getFormattedDate({ date: row.cell.value })}</span>;
                        }
                    },
                    {
                        Header: 'Status',
                        accessor: 'approved',
                        Cell: (row: any) => {
                            return <ApprovalStatusBadge status={row.cell.value} />;
                        }
                    },

                    ...(tableRowActions ? [{
                        Header: "Action",
                        id: 'Action',
                        Cell: (row: any) => {
                            return (
                                <div className="cursor-pointer flex justify-center !p-0 w-10">
                                    {tableRowActions?.(row.row.original).length > 0 &&
                                        <PopOnMe actions={tableRowActions?.(row.row.original)}>
                                            <EllipsisVerticalIcon className="h-6 w-6" />
                                        </PopOnMe>}
                                </div>
                            );
                        }
                    }] : []),
                ]
            }
        ],
        []
    );

    const getSingleLoan = async () => {
        if (!loanIdNumber) return;

        try {
            const response: any = await getLoanById(loanIdNumber)
            setLoanDetails(response.data);
            if (response && response?.data?.disbursementId) {
                const disbursementResponse: any = await fetchDisbursementById(response?.data?.disbursementId);
                setDisbursementDetails(disbursementResponse.data);
            }
        } catch (error) {

        }
    };

    const handleAddLoanToDisbursement = async (disbursementId: number, loans: Loan[]) => {
        try {
            const response: any = await addLoansToDisbursement(disbursementId, loans);
            if (response) {
                showToast.success(response?.message || 'Loan added to disbursement successfully');
                queryClient.invalidateQueries({ queryKey: ['fetchAllDisbursements'] });
            }
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to add loan to disbursement');
        }
    }

    const handleRemoveLoanFromDisbursement = async (disbursementId?: number, loanId?: number) => {
        if (!disbursementId || !loanId) return;
        try {
            const response: any = await removeLoanFromDisbursement(disbursementId, loanId);
            if (response) {
                showToast.success(response?.message || 'Loan removed from disbursement successfully');
                queryClient.invalidateQueries({ queryKey: ['fetchAllDisbursements'] });
            }
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to remove loan from disbursement');
        }
    }

    const fetchLoanPaymentHistoryByReference = async (reference: string) => {
        if (!reference) return [];
        try {
            const response = await getLoanPaymentHistoryByReference(reference);
            // return response?.filter((payment: any) => payment?.planType !== 'INITIAL_AMOUNT') || [];
            return response;
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to fetch loan payment history');
            return [];
        }
    }

    const regeneratePaymentsHistory = async (reference: string) => {
        if (!reference) return [];
        try {
            const response = await regeneratePaymentHistory(reference)

            if (response) {
                showToast.success(response?.message || "Regenerating loan payments successfull");
                queryClient.invalidateQueries({ queryKey: ['fetchLoanPaymentHistoryByReference'] });
            }
            return response;
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to regenerate');
            return [];
        }
    }

    const paymentHistoryColumns = useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'Due Date',
                        accessor: 'dueDate',
                        Cell: (row: any) => {
                            return (
                                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
                            );
                        }
                    },
                    {
                        Header: 'Paid Date',
                        accessor: 'paidDate',
                        Cell: (row: any) => {
                            return (
                                <span>{row.cell.value ? moment(row.cell.value).format('DD/MM/YYYY') : '-'}</span>
                            );
                        }
                    },
                    {
                        Header: 'Channel',
                        accessor: 'paymentMethod',
                        Cell: (row: any) => {
                            return <span className="">{getFormattedPaymentChannel(row.cell.value)}</span>;
                        }
                    },
                    {
                        Header: 'Processor',
                        accessor: '',
                        Cell: (row: any) => {
                            return <span className="">{row?.row?.original?.paymentMethod ?? '-'}</span>;
                        }
                    },
                    {
                        Header: 'Amount',
                        accessor: '',
                        Cell: (row: any) => {
                            const isInitial = row?.row?.original?.planType === 'INITIAL_AMOUNT';
                            return (
                                <span>{!isInitial ? loanDetails?.monthlyPaymentAmount ?? '-' : `${initialAmount.amount}`}</span>
                            );
                        }
                    },
                    {
                        Header: 'Status',
                        accessor: 'paymentStatus',
                        Cell: (row: any) => {
                            return <PaymentStatus row={row} />;
                        }
                    }
                ]
            }
        ],
        [loanDetails]
    );

    const handleRejectLoan = (loanId: number) => {
        updateModalStates({
            show: true,
            children: (
                <RejectLoanModal
                    onProceed={(reason: string) => {
                        const payload = {
                            reason: reason
                        }
                        rejectSingleLoan(Number(loanId || loanIdNumber), payload)
                            .then((response: any) => {
                                closeModal();
                                showToast.success(response?.message || "Loan rejected successful");
                                invalidateQueries();

                            })
                            .catch((error: any) => {
                                console.log(error);
                                closeModal();
                                showToast.error(error.description);
                            });
                    }}
                    onClose={closeModal}
                />
            ),
            size: 'sm',
            title: 'Reject Loan Application',
            showTitleSection: true,
        });
    }

    const approveLoanModal = (id?: number) => {
        updateModalStates({
            show: true,
            children: (
                <ActionConfirmation
                    action="approve"
                    type='success'
                    resourceType={''}
                    resourceName={''}
                    onClose={closeModal}
                    onConfirm={() => {
                        approveSingleLoan(id ? id : Number(loanIdNumber))
                            .then((response: any) => {
                                closeModal();
                                showToast.success(response?.message || "Loan approved successful");
                                invalidateQueries();

                            })
                            .catch((error: any) => {
                                console.log(error);
                                closeModal();
                                showToast.error(error.description);
                            });
                    }}
                />
            ),
            size: 'sm',
            showTitleSection: true,
        });
    }

    const handleGenerateLoanAgreement = () => {
        updateModalStates({
            show: true,
            children: (
                <ActionConfirmation
                    action="approve"
                    type='success'
                    resourceType={''}
                    resourceName={''}
                    onClose={closeModal}
                    onConfirm={() => {
                        generateLoanAgreementDocuments(String(loanDetails?.id))
                            .then((response: any) => {
                                closeModal();
                                showToast.success(response?.message || "Loan approved successful");
                                invalidateQueries();

                            })
                            .catch((error: any) => {
                                console.log(error);
                                closeModal();
                                showToast.error(error.description);
                            });
                    }}
                />
            ),
            size: 'sm',
            showTitleSection: true,
        });
    }

    const handleCheckLoanStatus = async () => {
        try {
            const response: any = await verifyLoanAndReconcileLoanStatus(String(loanIdNumber))
            if (response) {
                invalidateQueries();
                showToast.success(response?.message || "Loan status checked successfully");
            }
        } catch (error: any) {
            showToast.error(error?.description || 'Failed to check loan status');
        }
    }

    const removeLoanById = async (loanApplicationNumber: number) => {
        updateModalStates({
            show: true,
            title: 'Delete Loan',
            size: 'sm',
            children: (
                <ActionConfirmation
                    action="delete this loan"
                    type='danger'

                    onClose={() => {
                        closeModal()
                    }}
                    onConfirm={async () => {
                        try {
                            if (!loanApplicationNumber) return;
                            const response: any = await deleteLoanById(loanApplicationNumber);
                            if (response && !loanId) {
                                showToast.success(response?.message);
                                closeModal();
                                queryClient.invalidateQueries({ queryKey: ['fetchAllLoans'] });
                            }

                            if (response && loanId) {
                                showToast.success(response?.message);
                                closeModal();
                                navigate(`${getDashboardBase()}/loans`);
                            }
                        } catch (error: any) {
                            closeModal();
                            showToast.error(error?.description || 'Failed to delete loan');
                        }
                    }}

                />
            ),
            showCloseButton: false,
            classes: '!p-0 r-[5px]'
        })
    }

    const markALoanAsPaid = async (loan: any) => {
        updateModalStates({
            show: true,
            size: 'sm',
            children: (
                <ActionConfirmation
                    action="mark as paid"
                    type='success'

                    onClose={() => {
                        closeModal()
                    }}
                    onConfirm={async () => {
                        try {
                            if (!loan.id) return;
                            const response: any = await markLoanAsPaid(loan.id);
                            if (response) {
                                showToast.success(response?.message || 'Loan marked as paid successfully');
                                closeModal();
                                queryClient.invalidateQueries({ queryKey: ['fetchAllLoans'] })
                            }
                        } catch (error: any) {
                            showToast.error(error?.description || 'Failed to mark loan as paid');
                        }
                    }
                    }
                />),
            showCloseButton: false,
            classes: '!p-0 r-[5px]'
        })

    }

    return {
        columns,
        paymentHistoryColumns,
        LoanModalComponent,
        getRouteFromProductType,
        handleAddLoanToDisbursement,
        handleRemoveLoanFromDisbursement,
        fetchLoanPaymentHistoryByReference,
        handleGenerateLoanAgreement,
        regeneratePaymentsHistory,
        handleRejectLoan,
        approveLoanModal,
        invalidateQueries,
        paymentHistory,
        paymentHistoryLoading,
        initialAmount,
        loanDetailsLoading,
        loanDetails,
        disbursementDetails,
        handleCheckLoanStatus,
        removeLoanById,
        ApprovalStatusBadge,
        markALoanAsPaid
    }
}

export default useLoans;

